import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useWhatsAppModalStyles from "../WhatsAppModal/whatsAppModalStyles";
import useWhatsAppModal from "../WhatsAppModal/useWhatsAppModal";
import TabPanel from "../WhatsAppModal/TabPanel";
import TransferTab from "../WhatsAppModal/TransferTab";
import AutomationSection from "../WhatsAppModal/AutomationSection";
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
import ConnectionWarningModal from "../ConnectionWarningModal";
import BaileysIdentitySection from "./BaileysIdentitySection";
import HistoryPolicySection from "./HistoryPolicySection";
import { getBaileysSyncPolicy, saveBaileysSyncPolicy } from "../../services/baileysSyncPolicy.api";
import { COMPACT_MENU } from "./compactMenu";

/**
 * Modal PROPIO de las conexiones API Baileys (doc 0439 S6), crear y editar.
 *
 * WHY no se rediseñó `WhatsAppModal`: lo comparten el botón «Colas, flujo e
 * IA…» de Telegram (0438) y cualquier proveedor sin modal propio. Cambiarlo los cambiaba a todos. Este modal reutiliza su lógica entera
 * (`useWhatsAppModal`: carga, validación, preview de cambios, guardado) y sus secciones de
 * automatización y transferencias; lo nuevo es solo la maqueta compacta, el token con icono de
 * ojo y la sección «Historial al vincular».
 */

const SessionSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
});

const DEFAULT_POLICY = { historyMode: "off", historyDays: 7, historyMaxPerChat: 200, historyIncludeGroups: false };

const samePolicy = (a, b) =>
  a.historyMode === b.historyMode &&
  Number(a.historyDays) === Number(b.historyDays) &&
  Number(a.historyMaxPerChat) === Number(b.historyMaxPerChat) &&
  Boolean(a.historyIncludeGroups) === Boolean(b.historyIncludeGroups);

const useStyles = makeStyles((theme) => ({
  paper: { maxWidth: 680 },
  title: { padding: theme.spacing(1.5, 2.5), "& h2": { fontSize: "1.05rem", fontWeight: 600 } },
  content: {
    padding: theme.spacing(1, 2.5, 2),
    // Densidad uniforme también para las secciones reutilizadas del modal genérico.
    "& .MuiOutlinedInput-root": { fontSize: "0.85rem" },
    "& .MuiInputLabel-outlined": { fontSize: "0.85rem" },
    "& .MuiFormControl-marginDense": { marginTop: 0, marginBottom: 0 },
    "& .MuiFormControlLabel-label": { fontSize: "0.85rem" },
  },
  tabs: { minHeight: 36, marginBottom: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.divider}` },
  tab: { minHeight: 36, fontSize: "0.8rem" },
  section: {
    margin: theme.spacing(1.5, 0, 0.75),
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  hint: { display: "block", marginTop: 2, lineHeight: 1.35 },
  actions: { padding: theme.spacing(1.25, 2.5) },
}));

const Seccion = ({ children, classes }) => (
  <Grid item xs={12}>
    <Typography className={classes.section}>{children}</Typography>
  </Grid>
);

const BaileysConnectionModal = ({ open, onClose, whatsAppId, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const legacyClasses = useWhatsAppModalStyles();

  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const policyOriginal = useRef(DEFAULT_POLICY);
  const [policyLoading, setPolicyLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!whatsAppId) {
      setPolicy(DEFAULT_POLICY);
      policyOriginal.current = DEFAULT_POLICY;
      return;
    }
    let vivo = true;
    setPolicyLoading(true);
    getBaileysSyncPolicy(whatsAppId)
      .then(({ data }) => {
        if (!vivo) return;
        const p = {
          historyMode: data.historyMode,
          historyDays: data.historyDays,
          historyMaxPerChat: data.historyMaxPerChat,
          historyIncludeGroups: data.historyIncludeGroups,
        };
        setPolicy(p);
        policyOriginal.current = p;
      })
      .catch(() => {
        if (!vivo) return;
        setPolicy(DEFAULT_POLICY);
        policyOriginal.current = DEFAULT_POLICY;
      })
      .finally(() => vivo && setPolicyLoading(false));
    return () => {
      vivo = false;
    };
  }, [open, whatsAppId]);

  // Tras guardar la conexión (el hook nos pasa su id, también si es nueva) se guarda la política,
  // solo si cambió: una conexión nueva en «No traer historial» no necesita fila (sin fila = off).
  const onGuardado = useCallback(
    async (guardada) => {
      const id = guardada?.id ?? whatsAppId;
      const p = policy;
      if (id && !samePolicy(p, policyOriginal.current)) {
        try {
          await saveBaileysSyncPolicy(id, p);
        } catch (err) {
          toast.warn("La conexión se guardó, pero no la opción de historial. Vuelve a abrirla para revisarla.");
          toastError(err);
        }
      }
      if (onSave) onSave(guardada);
    },
    [policy, whatsAppId, onSave]
  );

  const {
    activeTab,
    connectionWarningData,
    connectionWarningOpen,
    flowBuilders,
    formikInitialValues,
    handleChangeIntegration,
    handleChangePrompt,
    handleChangeQueue,
    handleCheckboxChange,
    handleClose,
    handleConfirmConfigChange,
    handleSaveWhatsApp,
    handleTabChange,
    isLoading,
    loadingFlowBuilders,
    loadingWhatsapps,
    previewData,
    previewModalOpen,
    selectedFlowBuilder,
    selectedIntegration,
    selectedPrompt,
    selectedQueueId,
    setActiveTab,
    setConnectionWarningData,
    setConnectionWarningOpen,
    setPendingWhatsAppData,
    setPreviewData,
    setPreviewModalOpen,
    setSelectedFlowBuilder,
    setSelectedIntegration,
    setSelectedPrompt,
    setSelectedQueueId,
    setTransferQueueId,
    setTransferWhatsappId,
    tabStates,
    transferQueueId,
    transferWhatsappId,
    whatsapps,
  } = useWhatsAppModal({ open, onClose, whatsAppId, onSave: onGuardado, defaultProvider: "baileys" });

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: classes.paper }}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle className={classes.title}>{whatsAppId ? "Editar conexión — API Baileys" : "Nueva conexión — API Baileys"}</DialogTitle>

        <Formik
          key={whatsAppId || "new"}
          initialValues={formikInitialValues}
          enableReinitialize
          validationSchema={SessionSchema}
          onSubmit={async (values, actions) => {
            if (!values.name || values.name.trim().length === 0) {
              setActiveTab(0);
              actions.setErrors({ name: "Required" });
              actions.setSubmitting(false);
              toast.error("Por favor completa los campos requeridos.");
              return;
            }
            await handleSaveWhatsApp(values);
            actions.setSubmitting(false);
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => {
            const hasPromptInForm = !!selectedPrompt;
            const hasIntegrationInForm = !!selectedIntegration;
            const hasFlowBuilderInForm = !!selectedFlowBuilder;
            const hasQueueInForm = !!(selectedQueueId && selectedQueueId !== 0);
            const ocupado = isSubmitting || isLoading;

            return (
              <Form>
                <DialogContent dividers className={classes.content}>
                  <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" className={classes.tabs}>
                    <Tab label="Conexión" className={classes.tab} />
                    <Tab label="Transferencias" className={classes.tab} />
                  </Tabs>

                  <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={1}>
                      <Seccion classes={classes}>Identidad</Seccion>
                      <BaileysIdentitySection values={values} setFieldValue={setFieldValue} />

                      <Seccion classes={classes}>Enrutado</Seccion>
                      <AutomationSection
                        classes={legacyClasses}
                        selectedQueueId={selectedQueueId}
                        handleChangeQueue={handleChangeQueue}
                        selectedPrompt={selectedPrompt}
                        handleChangePrompt={handleChangePrompt}
                        selectedIntegration={selectedIntegration}
                        handleChangeIntegration={handleChangeIntegration}
                        selectedFlowBuilder={selectedFlowBuilder}
                        setSelectedFlowBuilder={setSelectedFlowBuilder}
                        setSelectedPrompt={setSelectedPrompt}
                        setSelectedIntegration={setSelectedIntegration}
                        setSelectedQueueId={setSelectedQueueId}
                        loadingFlowBuilders={loadingFlowBuilders}
                        flowBuilders={flowBuilders}
                        hasPromptInForm={hasPromptInForm}
                        hasIntegrationInForm={hasIntegrationInForm}
                        hasFlowBuilderInForm={hasFlowBuilderInForm}
                        hasQueueInForm={hasQueueInForm}
                        menuProps={COMPACT_MENU}
                      />
                      <Grid item xs={12}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <InputLabel id="baileys-purpose-label">Para qué sirve esta conexión</InputLabel>
                          <Select
                            labelId="baileys-purpose-label"
                            label="Para qué sirve esta conexión"
                            MenuProps={COMPACT_MENU}
                            value={values.type === "internal" ? "internal" : "attention"}
                            onChange={(e) => setFieldValue("type", e.target.value === "internal" ? "internal" : "")}
                          >
                            <MenuItem value="attention">Atención al cliente</MenuItem>
                            <MenuItem value="internal">Número interno o personal</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary" className={classes.hint}>
                          {values.type === "internal"
                            ? "Sus conversaciones entran al CRM, pero no se reparten ni las contesta el bot. Ojo: un número personal vuelca conversaciones privadas, visibles para los agentes."
                            : "Reparto a departamentos y automatizaciones activas."}
                        </Typography>
                      </Grid>

                      <Seccion classes={classes}>Historial al vincular</Seccion>
                      <HistoryPolicySection policy={policy} onChange={setPolicy} disabled={policyLoading || ocupado} menuProps={COMPACT_MENU} />

                      <Seccion classes={classes}>Leads web</Seccion>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              color="primary"
                              checked={!!values.webLeadsInstantMessage}
                              onChange={(e) => setFieldValue("webLeadsInstantMessage", e.target.checked)}
                            />
                          }
                          label="Mandar mensaje inmediato a Leads Web nuevos"
                        />
                        <Typography variant="caption" color="textSecondary" className={classes.hint}>
                          {values.webLeadsInstantMessage
                            ? "El negocio escribe primero en cuanto entra un lead. Si el número nunca contactó antes, WhatsApp puede bloquear la línea."
                            : "Modo seguro: el lead queda en «Solicitudes web» hasta que el cliente escriba primero."}
                        </Typography>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={1}>
                    <TransferTab
                      classes={legacyClasses}
                      tabStates={tabStates}
                      handleCheckboxChange={handleCheckboxChange}
                      setFieldValue={setFieldValue}
                      transferQueueId={transferQueueId}
                      setTransferQueueId={setTransferQueueId}
                      transferWhatsappId={transferWhatsappId}
                      setTransferWhatsappId={setTransferWhatsappId}
                      loadingWhatsapps={loadingWhatsapps}
                      whatsapps={whatsapps}
                      theme={theme}
                      menuProps={COMPACT_MENU}
                    />
                  </TabPanel>
                </DialogContent>

                <DialogActions className={classes.actions}>
                  <Button onClick={handleClose} disabled={ocupado} size="small">
                    {i18n.t("whatsappModal.buttons.cancel")}
                  </Button>
                  <Button type="submit" color="primary" variant="contained" size="small" disabled={ocupado}>
                    {whatsAppId ? i18n.t("whatsappModal.buttons.okEdit") : i18n.t("whatsappModal.buttons.okAdd")}
                    {ocupado && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingWhatsAppData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="whatsapp"
      />
    </>
  );
};

export default React.memo(BaileysConnectionModal);
